import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, Field, Input } from '@aions/ui-primitives';
import { loginWithPassword } from '../lib/auth-client';
import { useSession } from '../contexts/SessionContext';

export function LoginPage({ onSuccess }: { onSuccess: () => void }): JSX.Element {
  const { t } = useTranslation();
  const { setSession } = useSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const result = await loginWithPassword(email, password, totp);
    setLoading(false);
    if (!result.ok || !result.session) {
      setError(result.error ?? 'invalid_credentials');
      return;
    }
    setSession(result.session);
    onSuccess();
  };

  return (
    <div style={{ maxWidth: 440, margin: '40px auto' }}>
      <Card>
        <h1>{t('login.title')}</h1>

        {error && (
          <Alert variant="error" style={{ marginBottom: 16 }}>
            {t(`login.errors.${error}`, { defaultValue: t('login.errors.invalid_credentials') })}
          </Alert>
        )}

        <form onSubmit={(e) => void handleSubmit(e)} aria-label={t('login.title')}>
          <Field label={t('login.email_label')}>
            {({ id }) => (
              <Input
                id={id}
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            )}
          </Field>

          <Field label={t('login.password_label')}>
            {({ id }) => (
              <Input
                id={id}
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={12}
              />
            )}
          </Field>

          <Field label={t('login.totp_label')} help={t('login.totp_help')}>
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                mono
                value={totp}
                onChange={(e) => setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                pattern="[0-9]{6}"
                maxLength={6}
                style={{ letterSpacing: '0.4em' }}
              />
            )}
          </Field>

          <Button type="submit" block loading={loading}>
            {t('login.submit')}
          </Button>
        </form>
      </Card>
    </div>
  );
}
